import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Send, Calendar, Trash2, Pencil, Check, CheckCircle, Instagram, Facebook,
  ChevronLeft, ChevronRight, Heart, MessageCircle, Bookmark, MoreHorizontal,
  ThumbsUp, MessageSquare as FbComment, Share as FbShare, Wand2, Loader2
} from 'lucide-react';
import api from '../services/api';
import { generateStoryPreview } from '../services/storyApi';
import { DEFAULT_PUBLICATION_EVENTS } from '../constants/settings';
import { STOCK_STATUS } from '../constants/vehicleStatus';

/**
 * Derive the event type from a vehicle (same logic as SocialSection).
 */
const getVehicleEventType = (vehicle) => {
  if (vehicle.stock_status === STOCK_STATUS.SOLD) return 'sold_vehicle';
  if (vehicle.price_reduced) return 'price_reduction';
  return 'new_vehicle';
};

/**
 * Determine which platform/format combos are enabled based on user settings
 * and the vehicle's event type — uses the publication_events matrix (same
 * source of truth as the SocialSection cards).
 */
const getEnabledPlatforms = (settings, eventType) => {
  const pubEvents = settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;
  const eventConfig = pubEvents[eventType] || {};

  const platforms = [];

  if (eventConfig.instagram_story) {
    platforms.push({
      key: 'ig_story',
      label: 'Story',
      icon: Instagram,
      platform: 'instagram',
      format: 'story',
      color: 'pink',
    });
  }
  if (eventConfig.instagram_post) {
    platforms.push({
      key: 'ig_feed',
      label: 'Feed',
      icon: Instagram,
      platform: 'instagram',
      format: 'feed',
      color: 'pink',
    });
  }
  if (eventConfig.facebook_post) {
    platforms.push({
      key: 'fb_feed',
      label: 'Facebook',
      icon: Facebook,
      platform: 'facebook',
      format: 'feed',
      color: 'blue',
    });
  }

  // Fallback: if nothing enabled show all
  if (platforms.length === 0) {
    return [
      { key: 'ig_story', label: 'Story', icon: Instagram, platform: 'instagram', format: 'story', color: 'pink' },
      { key: 'ig_feed', label: 'Feed', icon: Instagram, platform: 'instagram', format: 'feed', color: 'pink' },
      { key: 'fb_feed', label: 'Facebook', icon: Facebook, platform: 'facebook', format: 'feed', color: 'blue' },
    ];
  }

  return platforms;
};

/**
 * Instagram feed-style preview
 */
const InstagramFeedPreview = ({ vehicle, caption, currentImage, onPrevImage, onNextImage, imageCount }) => (
  <div className="bg-dark-surface rounded-lg border border-dark-border overflow-hidden">
    <div className="flex items-center px-3 py-2.5">
      <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-orange-400 rounded-full flex items-center justify-center text-white text-xs font-bold">
        {vehicle.make?.[0] || 'C'}
      </div>
      <span className="ml-2.5 text-sm font-semibold text-primary">votre_garage</span>
      <MoreHorizontal className="h-5 w-5 text-secondary ml-auto" />
    </div>

    <div className="relative aspect-square bg-dark-bg">
      {vehicle.images?.[currentImage] ? (
        <img
          src={vehicle.images[currentImage]}
          alt={`${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted">
          Pas d'image
        </div>
      )}
      {imageCount > 1 && (
        <>
          <button onClick={onPrevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1">
            <ChevronLeft className="h-4 w-4 text-white" />
          </button>
          <button onClick={onNextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1">
            <ChevronRight className="h-4 w-4 text-white" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {Array.from({ length: Math.min(imageCount, 5) }).map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === currentImage ? 'bg-carmin' : 'bg-white/70'}`} />
            ))}
          </div>
        </>
      )}
    </div>

    <div className="px-3 py-2 flex items-center">
      <Heart className="h-6 w-6 text-primary" />
      <MessageCircle className="h-6 w-6 text-primary ml-3" />
      <Send className="h-6 w-6 text-primary ml-3" />
      <Bookmark className="h-6 w-6 text-primary ml-auto" />
    </div>

    <div className="px-3 pb-3">
      <p className="text-sm text-label line-clamp-4">
        <span className="font-semibold text-primary mr-1">votre_garage</span>
        {caption}
      </p>
    </div>
  </div>
);

/**
 * Facebook feed-style preview
 */
const FacebookFeedPreview = ({ vehicle, caption, currentImage, onPrevImage, onNextImage, imageCount }) => (
  <div className="bg-dark-surface rounded-lg border border-dark-border overflow-hidden">
    <div className="flex items-center px-4 py-3">
      <div className="w-10 h-10 bg-carmin rounded-full flex items-center justify-center text-white text-sm font-bold">
        {vehicle.make?.[0] || 'C'}
      </div>
      <div className="ml-3">
        <p className="text-sm font-semibold text-primary">Votre Page</p>
        <p className="text-xs text-muted">Maintenant</p>
      </div>
      <MoreHorizontal className="h-5 w-5 text-muted ml-auto" />
    </div>

    <div className="px-4 pb-3">
      <p className="text-sm text-label line-clamp-4 whitespace-pre-wrap">{caption}</p>
    </div>

    <div className="relative aspect-[4/3] bg-dark-bg">
      {vehicle.images?.[currentImage] ? (
        <img
          src={vehicle.images[currentImage]}
          alt={`${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted">
          Pas d'image
        </div>
      )}
      {imageCount > 1 && (
        <>
          <button onClick={onPrevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1">
            <ChevronLeft className="h-4 w-4 text-white" />
          </button>
          <button onClick={onNextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1">
            <ChevronRight className="h-4 w-4 text-white" />
          </button>
        </>
      )}
    </div>

    <div className="px-4 py-2 border-t border-dark-border flex justify-around">
      <button className="flex items-center text-secondary text-sm hover:bg-dark-hover px-3 py-1.5 rounded">
        <ThumbsUp className="h-5 w-5 mr-1.5" /> J'aime
      </button>
      <button className="flex items-center text-secondary text-sm hover:bg-dark-hover px-3 py-1.5 rounded">
        <FbComment className="h-5 w-5 mr-1.5" /> Commenter
      </button>
      <button className="flex items-center text-secondary text-sm hover:bg-dark-hover px-3 py-1.5 rounded">
        <FbShare className="h-5 w-5 mr-1.5" /> Partager
      </button>
    </div>
  </div>
);

/**
 * Instagram Story preview (9:16 ratio, rendered by story engine)
 */
const StoryPreview = ({ previewUrl, loading, error, onRetry }) => (
  <div className="flex flex-col items-center">
    <div className="aspect-[9/16] bg-gray-900 rounded-xl overflow-hidden w-full max-w-[260px] mx-auto">
      {loading ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-primary">
          <Loader2 className="h-8 w-8 animate-spin mb-3" />
          <p className="text-sm text-secondary">Génération du preview...</p>
        </div>
      ) : error ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-primary p-4">
          <p className="text-sm text-red-400 mb-3 text-center">{error}</p>
          <button onClick={onRetry} className="text-sm text-accent-purple-light hover:text-accent-purple">
            Réessayer
          </button>
        </div>
      ) : previewUrl ? (
        <img
          src={previewUrl}
          alt="Story preview"
          className="w-full h-full object-contain"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted">
          Aucun aperçu
        </div>
      )}
    </div>
    <p className="text-xs text-muted text-center mt-2">
      Modifiez le template dans Paramètres &rarr; Stories
    </p>
  </div>
);


/**
 * PublicationPreviewModal
 *
 * Shows a realistic preview of how the post will look, respecting user's
 * enabled platform settings (Instagram Story, Instagram Feed, Facebook Feed).
 */
const PublicationPreviewModal = ({ vehicle, userSettings, onClose, onPublished, onDeleted, initialPlatform, initialFormat }) => {
  const eventType = getVehicleEventType(vehicle);
  const enabledPlatforms = getEnabledPlatforms(userSettings, eventType);
  const initialKey = initialPlatform && initialFormat
    ? enabledPlatforms.find(p => p.platform === initialPlatform && p.format === initialFormat)?.key || enabledPlatforms[0]?.key
    : enabledPlatforms[0]?.key;
  const [activePlatformKey, setActivePlatformKey] = useState(initialKey || 'ig_story');
  const [currentImage, setCurrentImage] = useState(0);
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [editCaption, setEditCaption] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const [savedCaption, setSavedCaption] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const [refineInstruction, setRefineInstruction] = useState('');
  const [showRefine, setShowRefine] = useState(false);
  const [refining, setRefining] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [showScheduleInputs, setShowScheduleInputs] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [error, setError] = useState('');
  const [localVehicle, setLocalVehicle] = useState(vehicle);
  const refineRef = useRef(null);

  // Story preview state
  const [storyPreviewUrl, setStoryPreviewUrl] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyError, setStoryError] = useState('');

  const activePlatform = enabledPlatforms.find(p => p.key === activePlatformKey) || enabledPlatforms[0];
  const isStory = activePlatform?.format === 'story';

  const contentSource = eventType === 'sold_vehicle' && localVehicle.sold_content
    ? localVehicle.sold_content
    : eventType === 'price_reduction' && localVehicle.promo_content
      ? localVehicle.promo_content
      : localVehicle.generated_content;
  const caption = contentSource?.caption || '';
  const hashtags = contentSource?.hashtags || [];
  const fullCaption = hashtags.length > 0
    ? `${caption}\n\n${hashtags.join(' ')}`
    : caption;
  const imageCount = localVehicle.images?.length || 0;

  // Load story preview when story tab is active
  useEffect(() => {
    if (isStory && !storyPreviewUrl && !storyLoading) {
      loadStoryPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlatformKey]);

  const loadStoryPreview = async () => {
    setStoryLoading(true);
    setStoryError('');
    try {
      const result = await generateStoryPreview(localVehicle.id, eventType);
      setStoryPreviewUrl(result.preview_url);
    } catch (err) {
      setStoryError(err.response?.data?.detail || 'Erreur lors de la génération de la story');
    } finally {
      setStoryLoading(false);
    }
  };

  const handlePrevImage = () => {
    setCurrentImage(prev => prev > 0 ? prev - 1 : imageCount - 1);
  };

  const handleNextImage = () => {
    setCurrentImage(prev => prev < imageCount - 1 ? prev + 1 : 0);
  };

  const saveCaption = useCallback(async (captionToSave) => {
    setSavingCaption(true);
    setSavedCaption(false);
    setError('');
    try {
      // Route to the correct endpoint based on event type
      const endpoint = eventType === 'sold_vehicle'
        ? `/vehicles/${localVehicle.id}/sold-content`
        : eventType === 'price_reduction'
          ? `/vehicles/${localVehicle.id}/promo-content`
          : `/vehicles/${localVehicle.id}/content`;
      const response = await api.put(endpoint, {
        caption: captionToSave,
      });
      setLocalVehicle(response.data);
      setSavedCaption(true);
      setTimeout(() => setSavedCaption(false), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingCaption(false);
    }
  }, [localVehicle.id, eventType]);

  // Auto-save debounce: save 1.5s after user stops typing
  useEffect(() => {
    if (!isEditingCaption) return;
    // Don't save if caption hasn't changed from the original
    if (editCaption === caption) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (editCaption.trim()) {
        saveCaption(editCaption);
      }
    }, 1500);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [editCaption, isEditingCaption, caption, saveCaption]);

  const handleRefine = async () => {
    if (!refineInstruction.trim()) return;
    setRefining(true);
    setError('');
    try {
      const response = await api.post(`/vehicles/${localVehicle.id}/refine`, {
        instruction: refineInstruction,
        event_type: eventType,
      });
      setLocalVehicle(response.data);
      setRefineInstruction('');
      setShowRefine(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setRefining(false);
    }
  };

  const handlePublishNow = async () => {
    setPublishing(true);
    setError('');
    try {
      // All formats (feed + story) go through the queue endpoint
      await api.post('/publications', {
        vehicle_id: localVehicle.id,
        platform: activePlatform.platform,
        format: activePlatform.format,
        event_type: eventType,
      });
      // Close modal immediately — publication is queued
      if (onPublished) onPublished();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setPublishing(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduledDate || !scheduledTime) {
      setError('Sélectionnez une date et une heure');
      return;
    }
    setScheduling(true);
    setError('');
    try {
      const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      await api.post('/publications/schedule', {
        vehicle_id: localVehicle.id,
        platform: activePlatform.platform,
        format: activePlatform.format,
        scheduled_at: scheduledAt,
        event_type: eventType,
      });
      if (onPublished) onPublished();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la programmation');
    } finally {
      setScheduling(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer ${localVehicle.make} ${localVehicle.model} ?`)) return;
    try {
      await api.delete(`/vehicles/${localVehicle.id}`);
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const colorMap = {
    pink: {
      active: 'border-carmin bg-carmin-50 text-carmin-light',
      inactive: 'border-dark-border text-muted hover:border-gray-500',
    },
    blue: {
      active: 'border-carmin bg-carmin-50 text-carmin-light',
      inactive: 'border-dark-border text-muted hover:border-gray-500',
    },
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-card rounded-xl max-w-lg w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-dark-surface border-b border-dark-border">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            Aperçu — {localVehicle.make} {localVehicle.model}
            {eventType === 'sold_vehicle' && (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-900/50 text-red-400">VENDU</span>
            )}
          </h2>
          <button onClick={onClose} className="text-muted hover:text-primary hover:bg-dark-hover rounded p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Platform toggle — only enabled platforms */}
          {enabledPlatforms.length > 1 && (
            <div className="flex gap-2">
              {enabledPlatforms.map((p) => {
                const Icon = p.icon;
                const colors = colorMap[p.color] || colorMap.blue;
                const isActive = activePlatformKey === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setActivePlatformKey(p.key)}
                    className={`flex-1 inline-flex items-center justify-center py-2 text-sm rounded-lg border transition-colors ${
                      isActive ? colors.active : colors.inactive
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-1.5" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Single platform indicator when only one is enabled */}
          {enabledPlatforms.length === 1 && (
            <div className="flex items-center justify-center gap-2 text-sm text-secondary bg-dark-hover rounded-lg py-2">
              {(() => { const Icon = enabledPlatforms[0].icon; return <Icon className="h-4 w-4" />; })()}
              {enabledPlatforms[0].label}
            </div>
          )}

          {/* Preview */}
          {isStory ? (
            <StoryPreview
              previewUrl={storyPreviewUrl}
              loading={storyLoading}
              error={storyError}
              onRetry={loadStoryPreview}
            />
          ) : activePlatform?.platform === 'instagram' ? (
            <InstagramFeedPreview
              vehicle={localVehicle}
              caption={fullCaption}
              currentImage={currentImage}
              onPrevImage={handlePrevImage}
              onNextImage={handleNextImage}
              imageCount={imageCount}
            />
          ) : (
            <FacebookFeedPreview
              vehicle={localVehicle}
              caption={fullCaption}
              currentImage={currentImage}
              onPrevImage={handlePrevImage}
              onNextImage={handleNextImage}
              imageCount={imageCount}
            />
          )}

          {/* Edit / Refine — only for feed posts (story text is template-driven) */}
          {!isStory && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setEditCaption(caption);
                  setIsEditingCaption(true);
                  setShowRefine(false);
                }}
                className="inline-flex items-center text-sm text-secondary hover:text-primary px-3 py-1.5 rounded-md border border-dark-border hover:bg-dark-hover"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Modifier le texte
              </button>
              <button
                onClick={() => {
                  setShowRefine(true);
                  setIsEditingCaption(false);
                  setTimeout(() => refineRef.current?.focus(), 100);
                }}
                className="inline-flex items-center text-sm text-accent-purple-light hover:text-accent-purple px-3 py-1.5 rounded-md border border-accent-purple/30 hover:bg-accent-purple-bg"
              >
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                Ajuster avec l'IA
              </button>
            </div>
          )}

          {/* Edit caption inline — auto-save */}
          {isEditingCaption && !isStory && (
            <div className="bg-dark-hover rounded-lg border border-dark-border p-3">
              <textarea
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                rows={5}
                className="w-full text-sm bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin placeholder-muted"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted">
                  {savingCaption && 'Sauvegarde auto...'}
                  {savedCaption && (
                    <span className="text-green-400">
                      <Check className="h-3 w-3 inline mr-0.5" />
                      Sauvegardé
                    </span>
                  )}
                  {!savingCaption && !savedCaption && editCaption !== caption && 'Sauvegarde auto activée'}
                </span>
                <button
                  onClick={() => setIsEditingCaption(false)}
                  className="text-sm text-muted hover:text-primary px-3 py-1.5"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}

          {/* AI Refine */}
          {showRefine && !isStory && (
            <div className="bg-accent-purple-bg rounded-lg border border-accent-purple/30 p-3">
              <label className="block text-sm font-medium text-accent-purple-light mb-2">
                Dites à l'IA quoi changer :
              </label>
              <div className="flex gap-2">
                <input
                  ref={refineRef}
                  type="text"
                  value={refineInstruction}
                  onChange={(e) => setRefineInstruction(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRefine()}
                  placeholder="Rends le texte plus court, ajoute la garantie..."
                  className="flex-1 text-sm bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-purple placeholder-muted"
                />
                <button
                  onClick={handleRefine}
                  disabled={refining || !refineInstruction.trim()}
                  className="inline-flex items-center px-3 py-2 text-sm bg-accent-purple text-white rounded-md hover:bg-accent-purple-light disabled:opacity-50"
                >
                  {refining ? '...' : 'Appliquer'}
                </button>
              </div>
            </div>
          )}

          {/* Schedule inputs */}
          {showScheduleInputs && (
            <div className="bg-dark-hover rounded-lg border border-dark-border p-3">
              <label className="block text-sm font-medium text-label mb-2">
                Programmer pour :
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="flex-1 text-sm bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
                />
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="flex-1 text-sm bg-dark-input border border-dark-border text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
                />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => setShowScheduleInputs(false)}
                  className="text-sm text-muted hover:text-primary px-3 py-1.5"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSchedule}
                  disabled={scheduling || !scheduledDate || !scheduledTime}
                  className="inline-flex items-center text-sm bg-carmin text-white px-3 py-1.5 rounded-md hover:bg-carmin-light disabled:opacity-50"
                >
                  <Calendar className="h-3.5 w-3.5 mr-1" />
                  {scheduling ? 'Programmation...' : 'Confirmer'}
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-sm text-red-400 bg-red-900/20 border border-red-900/30 p-3 rounded-md">
              {error}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="bg-dark-surface border-t border-dark-border px-4 py-3">
          <div className="flex gap-2">
            <button
              onClick={handlePublishNow}
              disabled={publishing || (!isStory && !caption)}
              className="flex-1 inline-flex items-center justify-center py-2.5 text-sm font-medium bg-carmin text-white rounded-lg hover:bg-carmin-light disabled:opacity-50 transition-colors"
            >
              <Send className="h-4 w-4 mr-1.5" />
              {publishing ? 'Publication...' : `Publier ${activePlatform?.label || ''}`}
            </button>
            <button
              onClick={() => setShowScheduleInputs(!showScheduleInputs)}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium border border-dark-border text-label rounded-lg hover:bg-dark-hover transition-colors"
            >
              <Calendar className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Programmer</span>
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium border border-red-900/30 text-red-400 rounded-lg hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicationPreviewModal;
